import { Link as RouterLink } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { SubjectLanguage } from '../types';
import type { DisplayState } from '../vocabulary';

import { RequirementStateChip } from './RequirementStateChip';

export interface MissingRequirementRowView {
  patientRequirementId: string;
  patientId: string;
  /**
   * Display name. Documented nullable — the backend returns None when the
   * patient record cannot be loaded — so the view falls back to a shortened id
   * rather than rendering a blank cell that reads as a nameless patient.
   */
  patientName?: string;
  /** Date of birth, to tell two same-named patients apart on a chase list. */
  patientDob?: string;
  state: DisplayState;
  /** "Requested by SMS 4 days ago", or "Assigned 12 Aug". */
  detail?: string;
}

export interface MissingRequirementListViewProps {
  rows: MissingRequirementRowView[];
  total: number;
  page: number;
  perPage: number;
  isLoading: boolean;
  isError: boolean;
  /** Name of the requirement being listed, e.g. "Payment method on file". */
  requirementLabel: string;
  subject: SubjectLanguage;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onPatientClick?: (patientId: string, rowIndex: number) => void;
}

/** Enough of the id to tell two rows apart without pretending to be a name. */
const shortId = (patientId: string) => `${patientId.slice(0, 8)}…`;

/**
 * AC-2: the reconciled list of patients whose requirement is open.
 *
 * The backend verifies staleness against the authority before responding, so a
 * patient with a saved card does not appear here even when a cache says
 * otherwise — the frontend deliberately does no filtering of its own.
 */
export const MissingRequirementListView = ({
  rows,
  total,
  page,
  perPage,
  isLoading,
  isError,
  requirementLabel,
  subject,
  onPageChange,
  onRetry,
  onPatientClick,
}: MissingRequirementListViewProps) => {
  const firstIndex = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastIndex = Math.min(page * perPage, total);
  const hasPrevious = page > 1;
  const hasNext = lastIndex < total;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h6">{requirementLabel}</Typography>
        <Typography variant="body2" color="text.secondary">
          {isLoading
            ? 'Checking…'
            : `${total.toLocaleString()} ${
                total === 1 ? subject.singular : subject.plural
              } still need this.`}
        </Typography>
      </Stack>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          We could not load this list.
        </Alert>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          Every active {subject.singular} meets this requirement.
        </Typography>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={`${subject.pluralCap} missing ${requirementLabel}`}>
              <TableHead>
                <TableRow>
                  <TableCell>{subject.singularCap}</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={row.patientRequirementId} hover>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/patient/${row.patientId}`}
                        onClick={() => onPatientClick?.(row.patientId, rowIndex)}
                        sx={{ fontWeight: 600 }}
                      >
                        {row.patientName ?? shortId(row.patientId)}
                      </Link>
                      {row.patientDob && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          DOB: {row.patientDob}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <RequirementStateChip state={row.state} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.detail ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}
          >
            <Typography variant="caption" color="text.secondary">
              {firstIndex}–{lastIndex} of {total.toLocaleString()}
            </Typography>
            <IconButton
              size="small"
              disabled={!hasPrevious}
              onClick={() => onPageChange(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={!hasNext}
              onClick={() => onPageChange(page + 1)}
              aria-label="Next page"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </>
      )}
    </Paper>
  );
};
